/**
 * The working context (owner rule): a record opened from a list or another record stays inside the screen the
 * user is working in, and Back returns to the page it was opened from — same tab, filters, search and page.
 *
 * Each history entry (by TanStack's __TSR_index) remembers which /m/:module/:screen it belongs to, so a page can
 * tell where it was opened from without every list passing state.
 */
const KEY = 'nav-trail';

export interface NavEntry {
  module: string;
  screen: string;
  kind: 'list' | 'detail';
}

/**
 * The router's own history: it writes a new entry to the browser a moment after it starts showing the page, so the
 * browser's history.state can still be the page before (Back then skipped a page). Set once by app/router.
 */
let routerHistory: { location: { state: unknown } } | null = null;
export function setNavHistory(h: { location: { state: unknown } }) {
  routerHistory = h;
}

const index = (): number => ((routerHistory?.location.state ?? window.history.state) as { __TSR_index?: number } | null)?.__TSR_index ?? 0;

function read(): Record<number, NavEntry> {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) ?? '{}') as Record<number, NavEntry>;
  } catch {
    return {};
  }
}

/** Remembers the screen of the current history entry (called by the screen host on every page). */
export function recordVisit(e: NavEntry) {
  try {
    const trail = read();
    const i = index();
    trail[i] = e;
    // Forget entries far behind and anything ahead (a new navigation replaced the forward history).
    for (const k of Object.keys(trail).map(Number)) if (k < i - 100 || k > i) delete trail[k];
    sessionStorage.setItem(KEY, JSON.stringify(trail));
  } catch {
    /* storage unavailable: Back falls back to each page's own link */
  }
}

/** The page this one was opened from, when it was a screen of the app. */
export function previousEntry(): NavEntry | null {
  return read()[index() - 1] ?? null;
}

/**
 * Back (owner rule, 2026-10-28): always to the page the user came from — whatever screen it is — when that page was
 * a page of the app in this tab. Only a page opened directly (new tab, typed address) uses its own fallback link.
 */
export function canGoBack(): boolean {
  return !!previousEntry();
}

/** Back in context: the previous page belongs to the same screen (its list, or the record that opened this). */
export function canGoBackInContext(module: string, screen: string): boolean {
  const p = previousEntry();
  return !!p && p.module === module && p.screen === screen;
}

/** The screen of the current address (/m/:module/:screen/...). */
export function currentScreen(): { module: string; screen: string } | null {
  const m = /^\/m\/([^/]+)\/([^/]+)/.exec(window.location.pathname);
  return m ? { module: m[1]!, screen: m[2]! } : null;
}

/** "?doc=module.screen": a record of another screen opened inside the current one. */
export function hostedDoc(): string | null {
  return new URLSearchParams(window.location.search).get('doc');
}
